<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Activités récentes ({{ totalRows }})</h1>
          <small class="text-muted">Suivi des actions réalisées dans le backoffice.</small>
        </div>

        <div class="d-flex gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">{{ errorMessage }}</div>

      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <div class="row g-2 align-items-end">
            <div v-if="canFilterByModule" class="col-lg-2 col-md-6">
              <label class="form-label small mb-1">Module</label>
              <input v-model="filters.module" type="text" class="form-control form-control-sm" placeholder="sales" />
            </div>

            <div class="col-lg-2 col-md-6">
              <label class="form-label small mb-1">Méthode</label>
              <select v-model="filters.method" class="form-select form-select-sm">
                <option value="">Toutes</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>

            <div class="col-lg-2 col-md-6">
              <label class="form-label small mb-1">Du</label>
              <input v-model="filters.date_from" type="date" class="form-control form-control-sm" />
            </div>

            <div class="col-lg-2 col-md-6">
              <label class="form-label small mb-1">Au</label>
              <input v-model="filters.date_to" type="date" class="form-control form-control-sm" />
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button type="button" class="btn btn-outline-secondary btn-sm btn-icon-label" @click="resetFilters">
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button type="button" class="btn btn-primary btn-sm btn-icon-label" @click="fetchActivities">
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        :response="activitiesResponse"
        :columns="tableColumns"
        :loading="isLoading"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        state-key="activity-logs.list"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher module, action, chemin..."
        empty-text="Aucune activité trouvée."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @state-restored="Object.assign(table, { page: $event.page, perPage: $event.perPage })"
        @refresh="fetchActivities"
      >
        <template #cell-created_at="{ row }">
          {{ $format.datetime(row.created_at) }}
        </template>

        <template #cell-user="{ row }">
          {{ row.user?.name || '-' }}
        </template>

        <template #cell-method="{ row }">
          <span class="badge bg-dark">{{ row.method || row.action }}</span>
        </template>

        <template #cell-status_code="{ row }">
          <span class="badge" :class="statusClass(row.status_code)">
            {{ row.status_code || '-' }}
          </span>
        </template>
      </BootstrapDataTable>
    </template>
  </AdminLayout>
</template>

<script>
import { defineComponent } from 'vue';
import {
  cleanFilterParams,
  countActiveFilters,
  extractApiTotal,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';
import { isSuperAdminUser, isSupportUser } from '@/support/permissions';

const FILTER_MODULE = 'activities';
const DEFAULT_FILTERS = {
  search: '',
  user_id: '',
  module: '',
  method: '',
  date_from: '',
  date_to: '',
};

export default defineComponent({
  name: 'ActivityLogsView',
  data() {
    return {
      activitiesResponse: null,
      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,
      table: {
        sortBy: 'created_at',
        sortDir: 'desc',
        page: 1,
        perPage: 20,
      },
      tableColumns: [
        { key: 'created_at', label: 'Date', sortable: true },
        { key: 'user', label: 'Utilisateur' },
        { key: 'module', label: 'Module', sortable: true },
        { key: 'method', label: 'Méthode', sortable: true, class: 'text-center' },
        { key: 'description', label: 'Action' },
        { key: 'path', label: 'Chemin' },
        { key: 'status_code', label: 'Statut', sortable: true, class: 'text-center' },
      ],
      isLoading: false,
      errorMessage: '',
    };
  },
  mounted() {
    this.restoreFilters();
    this.applyRouteQueryFilters();
    this.fetchActivities();
  },
  computed: {
    totalRows() {
      const fallback = Array.isArray(this.activitiesResponse?.data)
        ? this.activitiesResponse.data.length
        : 0;
      return extractApiTotal(this.activitiesResponse, fallback);
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
    currentUser() {
      return this.$store?.getters?.['auth/currentUser'];
    },
    canFilterByModule() {
      return isSuperAdminUser(this.currentUser) || isSupportUser(this.currentUser);
    },
  },
  watch: {
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(FILTER_MODULE, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(FILTER_MODULE, value);
    },
    '$route.query': {
      deep: true,
      handler() {
        this.applyRouteQueryFilters();
        this.table.page = 1;
        this.fetchActivities();
      },
    },
  },
  methods: {
    restoreFilters() {
      const state = loadModuleFilterState(FILTER_MODULE, DEFAULT_FILTERS);
      this.filters = state.values;
      if (!this.canFilterByModule) {
        this.filters.module = '';
      }
      this.filtersVisible = state.visible;
    },
    applyRouteQueryFilters() {
      this.filters.user_id = this.$route.query.user_id ? String(this.$route.query.user_id) : '';
      this.filters.module = this.canFilterByModule && this.$route.query.module ? String(this.$route.query.module) : '';
      this.filters.method = this.$route.query.method ? String(this.$route.query.method) : '';
    },
    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },
    async fetchActivities() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const params = {
          ...cleanFilterParams(this.filters),
          paginate: 1,
          per_page: this.table.perPage,
          page: this.table.page,
          sort_by: this.table.sortBy === 'created_at' ? 'id' : this.table.sortBy,
          sort_dir: this.table.sortDir,
        };

        if (!this.canFilterByModule) {
          delete params.module;
        }

        const res = await this.$api.get('/activities', { params });
        this.activitiesResponse = res.data;
      } catch (error) {
        console.error('Erreur activités', error);
        this.errorMessage = 'Impossible de charger les activités récentes.';
      } finally {
        this.isLoading = false;
      }
    },
    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.table.page = 1;
      this.fetchActivities();
    },
    handleTableSearch(search) {
      this.filters.search = search;
      this.table.page = 1;
      this.fetchActivities();
    },
    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.table.page = 1;
      this.fetchActivities();
    },
    handlePageChange(page) {
      this.table.page = page;
      this.fetchActivities();
    },

    handlePerPageChange(perPage) {
      this.table.perPage = perPage;
      this.table.page = 1;
      this.fetchActivities();
    },
    statusClass(status) {
      const code = Number(status);
      if (code >= 200 && code < 300) return 'bg-success';
      if (code >= 400) return 'bg-danger';
      return 'bg-secondary';
    },
  },
});
</script>
